import { useEffect, useState } from 'react';
import emailjs from '@emailjs/browser';
import { SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY } from './emailConfig';
import { contact } from './content';

const empty = { name: '', email: '', subject: '', message: '', website: '' };
const MESSAGE_MAX = 2000;
const COOLDOWN_MS = 60000;
const SENT_KEY = 'skillbridge-contact-sent-at';

// One message per invalid field, in the order the fields appear on the page.
function validate(values) {
  const errors = {};
  const name = values.name.trim();
  const email = values.email.trim();
  const message = values.message.trim();
  if (!name) errors.name = 'Enter your name.';
  else if (name.length > 80) errors.name = 'Keep your name to 80 characters or fewer.';
  if (!email) errors.email = 'Enter your email address.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Enter a valid email address, like name@example.com.';
  if (values.subject.trim().length > 120) errors.subject = 'Keep the subject to 120 characters or fewer.';
  if (!message) errors.message = 'Enter your message.';
  else if (message.length < 10) errors.message = 'Write at least 10 characters.';
  else if (message.length > MESSAGE_MAX) errors.message = `Keep your message to ${MESSAGE_MAX} characters or fewer.`;
  return errors;
}

// Milliseconds until this browser may send again. Storage can be blocked, and then nothing is held back.
function waitLeft() {
  try {
    return Math.max(0, COOLDOWN_MS - (Date.now() - Number(localStorage.getItem(SENT_KEY))));
  } catch {
    return 0;
  }
}

function Field({ name, label, error, hint, as: Tag = 'input', ...props }) {
  const describedBy = [error && `${name}-error`, hint && `${name}-hint`].filter(Boolean).join(' ');
  return (
    <div className="field">
      <label htmlFor={name}>{label}</label>
      <Tag id={name} name={name} aria-invalid={error ? 'true' : undefined} aria-describedby={describedBy || undefined} {...props} />
      {error && <p id={`${name}-error`} className="error">{error}</p>}
      {hint && <p id={`${name}-hint`} className="hint">{hint}</p>}
    </div>
  );
}

function Contact() {
  const [values, setValues] = useState(empty);
  const [errors, setErrors] = useState({});
  // idle, sending, sent, failed, or a number of seconds still to wait
  const [status, setStatus] = useState('idle');

  // The site is one long page, so arriving here from further down it would start scrolled.
  // The braces matter: scrollTo can return a promise, and an effect must not return one.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const set = (e) => setValues({ ...values, [e.target.name]: e.target.value });

  async function send(e) {
    e.preventDefault();
    const found = validate(values);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      e.target.elements[first].focus();
      return;
    }
    const wait = waitLeft();
    if (wait) {
      setStatus(Math.ceil(wait / 1000));
      return;
    }
    setStatus('sending');
    try {
      // A filled "website" field means a bot: report success and send nothing.
      if (!values.website) {
        await emailjs.send(SERVICE_ID, TEMPLATE_ID, {
          from_name: values.name.trim(),
          from_email: values.email.trim(),
          subject: values.subject.trim(),
          message: values.message.trim(),
        }, { publicKey: PUBLIC_KEY });
      }
    } catch (error) {
      // EmailJS gives the reason here, for example an invalid key or a used-up monthly quota.
      console.error('Contact form send failed:', error);
      setStatus('failed');
      return;
    }
    try {
      localStorage.setItem(SENT_KEY, String(Date.now()));
    } catch {
      // Storage is blocked, so the 60-second guard is skipped.
    }
    setValues(empty);
    setStatus('sent');
  }

  const sending = status === 'sending';
  return (
    <main id="main" className="wrap section-grid contact-page">
      <h1>{contact.title}</h1>
      <div className="section-body">
        <p>{contact.intro}</p>
        <form className="contact" onSubmit={send} noValidate>
          <Field name="name" label="Name" value={values.name} onChange={set} error={errors.name} maxLength={80} autoComplete="name" required />
          <Field name="email" label="Email" type="email" value={values.email} onChange={set} error={errors.email} autoComplete="email" required />
          <Field name="subject" label="Subject (optional)" value={values.subject} onChange={set} error={errors.subject} maxLength={120} />
          <Field
            as="textarea" name="message" label="Message" value={values.message} onChange={set} error={errors.message}
            hint={`${values.message.length} / ${MESSAGE_MAX}`} maxLength={MESSAGE_MAX} rows={7} required
          />
          <div className="hp" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input id="website" name="website" value={values.website} onChange={set} tabIndex={-1} autoComplete="off" />
          </div>
          <button className="pill" type="submit" disabled={sending}>{sending ? 'Sending…' : 'Send message'}</button>
          {status === 'sent' && <p className="notice ok" role="status">Thanks, your message was sent. We’ll reply by email.</p>}
          {status === 'failed' && (
            <p className="notice bad" role="alert">
              Sorry, your message could not be sent. Your text is still here, so you can try again, or
              email us at <a href={`mailto:${contact.email}`}>{contact.email}</a>.
            </p>
          )}
          {typeof status === 'number' && (
            <p className="notice bad" role="alert">Please wait {status} seconds before sending another message.</p>
          )}
        </form>
      </div>
    </main>
  );
}

export default Contact;
